import { useEffect, useRef, useState } from "react";

export interface PlaceSuggestion {
  // The whole line, e.g. "Bangkok, Thailand". What goes into the composer.
  label: string;
  // Its two halves, so the place reads in ink and the country in muted type.
  main: string;
  secondary: string;
  // Carried through from Google. Nothing consumes it yet; it is here so a
  // later change can skip a geocode for a destination the user picked.
  placeId: string;
}

// Long enough that a normal typist sends about three requests for one
// destination instead of one per keystroke, short enough that the list
// still feels attached to the keyboard.
const DEBOUNCE_MS = 220;
const MIN_LENGTH = 2;
// Place names are short. "Ho Chi Minh City" is the long end at four words,
// so anything past six is prose the user is writing about their trip, and
// asking Google about it would only spend requests to get nothing back.
const MAX_WORDS = 6;

// Destination suggestions for `query`, or an empty list when `enabled` is
// false. Every failure is an empty list on purpose: this sits under a
// free-text field that has always worked without it, so a rate limit, an
// unconfigured key or a dead network must never interrupt typing.
export function usePlaceSuggestions(
  query: string,
  enabled: boolean
): PlaceSuggestion[] {
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const trimmed = query.trim();
    const tooWordy = trimmed.split(/\s+/).length > MAX_WORDS;
    if (!enabled || trimmed.length < MIN_LENGTH || tooWordy) {
      abortRef.current?.abort();
      abortRef.current = null;
      setSuggestions([]);
      return;
    }

    const timer = setTimeout(() => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      fetch(`/api/place-suggest?q=${encodeURIComponent(trimmed)}`, {
        signal: ctrl.signal,
      })
        .then((res) => res.json())
        .then((data) => {
          if (ctrl.signal.aborted) return;
          setSuggestions(
            Array.isArray(data?.suggestions) ? data.suggestions : []
          );
        })
        .catch(() => {
          // An abort is a superseded keystroke, and the request that
          // replaced it will set the list. Anything else is a real
          // failure, and a real failure is an empty list, never the
          // answer to a word the user has already typed over.
          if (ctrl.signal.aborted) return;
          setSuggestions([]);
        });
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query, enabled]);

  // Drop the in-flight request if the composer goes away mid-keystroke.
  useEffect(() => () => abortRef.current?.abort(), []);

  return suggestions;
}
